/**
 * Choosing a preset when Auto show brings in a whole new look from your presets: in order, at
 * random, or by matching preset energy tags to the detected mood.
 */
import { energyBucket, type MoodState } from '../shared/mood';
import type { Settings } from '../shared/settings';
import { hash01 } from '../shared/modulation';

export interface Candidate {
  id: string;
  tags: string[];
}

/**
 * Choose the next preset. `recent` holds recently used ids to avoid repeats; `seed` makes shuffle
 * deterministic for tests.
 */
export function choosePreset(
  pool: Candidate[],
  currentId: string | null,
  mode: Settings['autopilotSelection'],
  mood: MoodState,
  recent: string[],
  seed: number,
): string | null {
  if (pool.length === 0) return null;
  if (pool.length === 1) return pool[0]!.id;
  if (mode === 'sequential') {
    const i = pool.findIndex((p) => p.id === currentId);
    return pool[(i + 1) % pool.length]!.id;
  }
  let candidates = pool.filter((p) => p.id !== currentId && !recent.includes(p.id));
  if (candidates.length === 0) candidates = pool.filter((p) => p.id !== currentId);
  if (mode === 'mood') {
    const want = energyBucket(mood.arousal);
    const matching = candidates.filter((p) => p.tags.includes(`energy:${want}`));
    if (matching.length > 0) candidates = matching;
  }
  return candidates[Math.floor(hash01(seed) * candidates.length)]!.id;
}
